// src/components/dashboard/cases/newCase/case_id/caseTable/components/Sidebar.js
import React from "react";
import { OPACITY_MAX } from "../constants";

/* ✅ NEW: sidebar is always fully open + shrinks on small screens.
   Scoped overrides only - desktop look stays the same. */
const SIDEBAR_RESPONSIVE_CSS = `
  /* always open: no collapse animation / hidden state */
  .stl-sidebar .stl-sidebar-collapsible.always-open,
  .stl-sidebar .stl-sidebar-collapsible.always-open .stl-sidebar-collapsible-inner {
    max-height: none !important;
    grid-template-rows: 1fr !important;
    overflow: visible !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  /* never taller than the viewer: scroll inside if the screen is short */
  .stl-sidebar {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
  }
  .stl-sidebar::-webkit-scrollbar { display: none; }

  /* ---- tablets ---- */
  @media (max-width: 991px) {
    .stl-sidebar .stl-sidebar-btn {
      padding: 6px 4px !important;
      font-size: 10px !important;
      min-height: 0 !important;
    }
    .stl-sidebar .stl-sidebar-btn svg {
      width: 18px !important;
      height: 18px !important;
    }
  }

  /* ---- phones ---- */
  @media (max-width: 767px) {
    .stl-sidebar .stl-sidebar-btn {
      padding: 4px 3px !important;
      font-size: 9px !important;
      gap: 2px !important;
      min-width: 0 !important;
      width: 52px !important;
    }
    .stl-sidebar .stl-sidebar-btn svg {
      width: 15px !important;
      height: 15px !important;
    }
    .stl-sidebar .stl-sidebar-label {
      font-size: 8px !important;
      margin-bottom: 2px !important;
    }
    .stl-sidebar .stl-sidebar-group { gap: 3px !important; }
    .stl-sidebar .stl-sidebar-divider { margin: 4px 0 !important; }
    .stl-sidebar { padding: 4px !important; }
  }

  /* ---- small phones ---- */
  @media (max-width: 400px) {
    .stl-sidebar .stl-sidebar-btn {
      width: 44px !important;
      font-size: 8px !important;
      padding: 3px 2px !important;
    }
    .stl-sidebar .stl-sidebar-btn svg {
      width: 13px !important;
      height: 13px !important;
    }
    .stl-sidebar .stl-sidebar-btn span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  /* ---- short screens (phone landscape) ---- */
  @media (max-height: 560px) {
    .stl-sidebar .stl-sidebar-btn {
      padding: 3px 2px !important;
      font-size: 8px !important;
    }
    .stl-sidebar .stl-sidebar-btn svg {
      width: 13px !important;
      height: 13px !important;
    }
    .stl-sidebar .stl-sidebar-label { font-size: 8px !important; }
  }
`;

const Sidebar = ({
  goTo,
  wireframe,
  setWireframe,
  showOpacity,
  setShowOpacity,
  autoRotate,
  setAutoRotate,
  showCrossSection,
  sectionDrawMode,
  handleSectionButton,
  measureMode,
  setMeasureMode,
  expanded,          // kept so the parent doesn't need changes (no longer used)
  toggleSidebar,     // kept so the parent doesn't need changes (no longer used)
  hasActiveDisplay,  // kept so the parent doesn't need changes (no longer used)
  opacity,
  dockClass,
  dockRef,
  onDockPointerDown,
}) => {
  return (
    <div
      ref={dockRef}
      className={dockClass}
      onPointerDown={onDockPointerDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      <style>{SIDEBAR_RESPONSIVE_CSS}</style>

      <aside className="stl-sidebar" aria-label="Viewer controls">
        <div className="stl-sidebar-section">
          <div className="stl-sidebar-group">
            <button className="stl-sidebar-btn" onClick={() => goTo("default")}
              title="Reset view" aria-label="Reset view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <path d="M3 4v5h5" />
              </svg>
              <span>Reset</span>
            </button>
            <button className="stl-sidebar-btn" onClick={() => goTo("top")}
              title="Top view" aria-label="Top view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" />
                <path d="M4 9h16" />
              </svg>
              <span>Top</span>
            </button>
            <button className="stl-sidebar-btn" onClick={() => goTo("bottom")}
              title="Bottom view" aria-label="Bottom view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" />
                <path d="M4 15h16" />
              </svg>
              <span>Bottom</span>
            </button>
            <button className="stl-sidebar-btn" onClick={() => goTo("front")}
              title="Front view" aria-label="Front view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="6" width="16" height="12" rx="2" />
              </svg>
              <span>Front</span>
            </button>
            <button className="stl-sidebar-btn" onClick={() => goTo("rear")}
              title="Rear view" aria-label="Rear view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="6" width="16" height="12" rx="2" />
                <path d="M8 10h8" />
              </svg>
              <span>Rear</span>
            </button>
            <button className="stl-sidebar-btn" onClick={() => goTo("left")}
              title="Left view" aria-label="Left view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 4h10v16H7z" />
                <path d="M11 8h4" />
              </svg>
              <span>Left</span>
            </button>
            <button className="stl-sidebar-btn" onClick={() => goTo("right")}
              title="Right view" aria-label="Right view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 4h10v16H7z" />
                <path d="M9 8h4" />
              </svg>
              <span>Right</span>
            </button>
          </div>
        </div>

        {/* ✅ CHANGED: always open (was toggled by `expanded`) */}
        <div
          id="stl-sidebar-more"
          className="stl-sidebar-collapsible open always-open"
        >
          <div className="stl-sidebar-collapsible-inner">
            <div className="stl-sidebar-collapsible-content">
              <div className="stl-sidebar-divider" />
              <div className="stl-sidebar-section">
                <div className="stl-sidebar-group">
                  <button
                    className={`stl-sidebar-btn${wireframe ? " active" : ""}`}
                    onClick={() => setWireframe((v) => !v)}
                    title="Toggle wireframe"
                    aria-label="Toggle wireframe"
                    aria-pressed={wireframe}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2 2 7l10 5 10-5-10-5z" />
                      <path d="m2 17 10 5 10-5" />
                      <path d="m2 12 10 5 10-5" />
                    </svg>
                    <span>Wireframe</span>
                  </button>
                  <button
                    className={`stl-sidebar-btn${
                      showOpacity || opacity < OPACITY_MAX ? " active" : ""
                    }`}
                    onClick={() => setShowOpacity((v) => !v)}
                    title="Opacity (see-through)"
                    aria-label="Opacity (see-through)"
                    aria-pressed={showOpacity}
                    aria-expanded={showOpacity}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
                    </svg>
                    <span>Opacity</span>
                  </button>
                  <button
                    className={`stl-sidebar-btn${autoRotate ? " active" : ""}`}
                    onClick={() => setAutoRotate((v) => !v)}
                    title="Toggle auto-rotate"
                    aria-label="Toggle auto-rotate"
                    aria-pressed={autoRotate}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-3-6.7" />
                      <path d="M21 3v5h-5" />
                    </svg>
                    <span>Auto-rotate</span>
                  </button>
                  <button
                    className={`stl-sidebar-btn${showCrossSection || sectionDrawMode ? " active" : ""}`}
                    onClick={handleSectionButton}
                    title="Draw a line on the model to open 2D cross section"
                    aria-label="2D cross section"
                    aria-pressed={showCrossSection || sectionDrawMode}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" />
                      <path d="M3 12h18" />
                      <path d="M3 18h18" />
                      <circle cx="9" cy="12" r="3" fill="currentColor" stroke="none" opacity="0.7" />
                    </svg>
                    <span>Section</span>
                  </button>
                  <button
                    className={`stl-sidebar-btn${measureMode ? " active" : ""}`}
                    onClick={() => setMeasureMode((v) => !v)}
                    title="Measure distance"
                    aria-label="Measure distance"
                    aria-pressed={measureMode}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.3 8.7 8.7 21.3a1 1 0 0 1-1.4 0l-4.6-4.6a1 1 0 0 1 0-1.4L15.3 2.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4z" />
                      <path d="m7.5 10.5 2 2" />
                      <path d="m10.5 7.5 2 2" />
                      <path d="m13.5 4.5 2 2" />
                      <path d="m4.5 13.5 2 2" />
                    </svg>
                    <span>Measure</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ CHANGED: expand/collapse toggle removed - sidebar is always open */}
      </aside>
    </div>
  );
};

export default Sidebar;